import React, { useState, useEffect } from 'react';
import { useReactFlow, useStore } from '@xyflow/react';
import { Select, Input, Space } from 'antd';
import styles from '../../timeline-node-manager.module.css';

const { Option } = Select;

interface Asset {
  id: string;
  name: string;
  url: string;
  previewImage?: string;
  ref: {
    current: {
      uuid: string;
    };
  };
}

interface SceneAssetProps {
  position?: any;
  scale?: any;
  rotation?: any;
  instanceId: string;
  asset: Asset;
}

interface ReactElement {
  props: SceneAssetProps;
  type: any;
  key: string;
}

interface TargetParamsProps {
  nodeId: string;
  selectableAssets: ReactElement[];
}

const TargetParams: React.FC<TargetParamsProps> = ({
  nodeId,
  selectableAssets,
}) => {
  const { setNodes } = useReactFlow();
  const node = useStore((state) => state.nodes.find((n) => n.id === nodeId));
  const target = node?.data ?? ({} as any);
  const [objectId, setObjectId] = useState(target.objectId);

  const onChangeNode = (key: string, value: string, additionalData = {}) => {
    setNodes((nodes) =>
      nodes.map((n) =>
        n.id === nodeId
          ? {
              ...n,
              data: {
                ...n.data,
                [key]: value,
                ...additionalData,
              },
            }
          : n
      )
    );
  };

  function onChangeObjectId(value: string) {
    const selectedAsset = selectableAssets.find((asset) => {
      const assetId = asset.props.asset?.id;
      const assetUuid = asset.props.asset?.ref?.current?.uuid;
      const instanceId = asset.props.instanceId;
      return assetId === value || assetUuid === value || instanceId === value;
    });

    if (selectedAsset?.props?.asset) {
      const previewImage =
        selectedAsset.props.asset.previewImage || selectedAsset.props.asset.url;
      const objectId =
        selectedAsset.props.asset?.id ||
        selectedAsset.props.asset?.ref?.current?.uuid ||
        selectedAsset.props.instanceId;

      setObjectId(objectId);
      onChangeNode('objectId', objectId, { previewImage });
    }
  }

  useEffect(() => {
    setObjectId(target.objectId);
  }, [node?.id]);

  return (
    <>
      <div>
        <Space direction='vertical' style={{ width: '100%' }}>
          <span className={styles.label}>Object:</span>
          {selectableAssets?.length > 0 ? (
            <Select
              value={objectId}
              onChange={onChangeObjectId}
              placeholder='Select object'
              style={{ width: '100%' }}
            >
              {selectableAssets?.map((asset) => {
                const assetId = asset.props.asset?.id;
                const assetUuid = asset.props.asset?.ref?.current?.uuid;
                const instanceId = asset.props.instanceId;
                const value = assetId || assetUuid || instanceId;
                const name = asset.props.asset?.name || 'Unnamed Asset';

                return (
                  <Option key={instanceId} value={value}>
                    {name}
                  </Option>
                );
              })}
            </Select>
          ) : (
            <Input
              value={objectId}
              onChange={(e) => onChangeObjectId(e.target.value)}
              placeholder={`Enter Object ID`}
            />
          )}
        </Space>
      </div>
    </>
  );
};

export default TargetParams;
